import type { CommonProject } from '../universal-nav/types';

/**
 * Find the project in `projects` whose id equals `projectId`.
 * Single source of truth for "which project is active" — used by both the
 * initial sync (`init`) and the `projectId` watcher so highlight + scroll
 * always target the same item.
 */
export function findSelectedProject(
  projects: CommonProject[],
  projectId: string | undefined
): CommonProject | undefined {
  if (!projectId) return undefined;
  return projects.find(({ project: { id } }) => id === projectId);
}

/** Minimal structural type for the SimpleBar-backed `ps-scrollbar`. */
type ScrollbarLike = Element & {
  simplebar?: { getScrollElement(): HTMLElement | null };
};

export interface MegaMenuScrollTarget {
  item: HTMLElement;
  container: HTMLElement;
}

/**
 * Within an open mega-menu popover, locate the selected project menu-item and
 * the scrollable container that needs to scroll it into view. Returns null when
 * either is missing (no active project, or the scrollbar isn't ready yet).
 *
 * Matches on the `selected` attribute because Lit's `?selected=` binding toggles
 * the attribute, which is exactly what is present in the rendered DOM.
 */
export function findMegaMenuScrollTarget(
  root: ParentNode | null | undefined
): MegaMenuScrollTarget | null {
  if (!root) return null;

  const items = Array.from(root.querySelectorAll<HTMLElement>('ps-menu-item'));
  const item = items.find((el) => el.hasAttribute('selected'));
  if (!item) return null;

  const scrollbar = root.querySelector<ScrollbarLike>(
    '.c-mega-menu__scrollbar'
  );
  const container = scrollbar?.simplebar?.getScrollElement() ?? null;
  if (!container) return null;

  return { item, container };
}
